import { ReviewTabs } from "../components/ReviewTabs";
import { Bookmark, CircleX, RotateCcw, Sprout } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { QUESTION_BY_ID } from "../data/questions";
import { domainName } from "../components/QuestionView";
import { Empty, useDocumentTitle } from "../components/ui";
import { DAY_MS } from "../lib/dates";
import { BOX_INTERVAL_DAYS, MAX_BOX } from "../lib/srs";
import { buildReview, dueQuestionIds, REVIEW_MAX, sessionStore } from "../lib/session";
import { useProgress } from "../lib/store";
import { EXAM_BY_ID } from "../data/exams";
import { useT, type L } from "../i18n";

export function ReviewPage() {
  const t = useT();
  useDocumentTitle(t("Ôn tập", "Review"));
  const p = useProgress();
  const navigate = useNavigate();
  const now = Date.now();
  const due = dueQuestionIds(p, now);
  const wrong = Object.entries(p.questions).filter(([id, s]) => !s.lastCorrect && QUESTION_BY_ID.has(id)).map(([id]) => id);
  const bookmarks = p.bookmarks.filter((id) => QUESTION_BY_ID.has(id));
  const boxes = Array.from({ length: MAX_BOX }, (_, i) => Object.values(p.questions).filter((s) => s.srs.box === i + 1).length);
  const upcoming = Object.values(p.questions).filter((s) => s.srs.due > now && s.srs.due <= now + DAY_MS).length;

  const start = (ids: string[], label: L) => {
    const s = buildReview(ids, label);
    if (!s) return;
    sessionStore.set(s);
    navigate("/session");
  };

  return (
    <>
      <h1>{t("Ôn tập thông minh", "Smart review")}</h1>
      <ReviewTabs />
      <p className="muted">
        {t(
          `Hệ thống Leitner 5 hộp: trả lời đúng → câu lên hộp cao hơn và quay lại sau ${BOX_INTERVAL_DAYS.slice(2).join(", ")} ngày; sai → về hộp 1 để ôn ngay. Ôn đúng lúc sắp quên là cách ghi nhớ lâu nhất.`,
          `A 5-box Leitner system: a correct answer moves the question up a box and it returns after ${BOX_INTERVAL_DAYS.slice(2).join(", ")} days; a wrong answer sends it back to box 1. Reviewing just before you forget builds the longest-lasting memory.`,
        )}
      </p>

      <div className="grid grid-3 mt">
        <div className="card">
          <h3><RotateCcw className="ico" size={20} aria-hidden="true" />{t("Đến hạn ôn", "Due now")}</h3>
          <div className="stat-value" style={{ fontSize: "2rem", fontWeight: 800 }}>{due.length}</div>
          <p className="small muted">{t(`${upcoming} câu sẽ đến hạn trong 24 giờ tới`, `${upcoming} more due in the next 24 hours`)}</p>
          <button type="button" className="btn primary" disabled={!due.length} onClick={() => start(due, { vi: "Ôn tập ngắt quãng", en: "Spaced review" })}>
            {t(`Ôn ${Math.min(due.length, REVIEW_MAX)} câu`, `Review ${Math.min(due.length, REVIEW_MAX)}`)}
          </button>
        </div>
        <div className="card">
          <h3><CircleX className="ico" size={20} aria-hidden="true" />{t("Đang trả lời sai", "Currently wrong")}</h3>
          <div style={{ fontSize: "2rem", fontWeight: 800 }}>{wrong.length}</div>
          <p className="small muted">{t("Câu có lần trả lời gần nhất là sai", "Your latest answer was wrong")}</p>
          <button type="button" className="btn" disabled={!wrong.length} onClick={() => start(wrong, { vi: "Luyện lại câu sai", en: "Retry wrong answers" })}>{t("Luyện câu sai", "Practice wrong")}</button>
        </div>
        <div className="card">
          <h3><Bookmark className="ico" size={20} aria-hidden="true" />{t("Đã đánh dấu", "Bookmarked")}</h3>
          <div style={{ fontSize: "2rem", fontWeight: 800 }}>{bookmarks.length}</div>
          <p className="small muted">{t("Câu bạn muốn xem lại", "Questions you saved")}</p>
          <button type="button" className="btn" disabled={!bookmarks.length} onClick={() => start(bookmarks, { vi: "Câu đã đánh dấu", en: "Bookmarked questions" })}>{t("Ôn câu đánh dấu", "Review bookmarks")}</button>
        </div>
      </div>

      <div className="section-title"><h2>{t("Phân bố theo hộp", "Box distribution")}</h2></div>
      <div className="card">
        <div className="grid grid-4" style={{ gridTemplateColumns: `repeat(${MAX_BOX}, 1fr)` }}>
          {boxes.map((n, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1.5rem", fontWeight: 800 }}>{n}</div>
              <div className="tiny muted">{t("Hộp", "Box")} {i + 1}{i >= 2 ? t(" · thành thạo", " · mastered") : ""}</div>
            </div>
          ))}
        </div>
        <p className="tiny muted mt mb-0">{t("Hộp 3 trở lên được tính là “thành thạo”.", "Box 3 and up counts as “mastered”.")}</p>
      </div>

      {bookmarks.length > 0 && (
        <>
          <div className="section-title"><h2>{t("Danh sách đánh dấu", "Bookmarks")}</h2></div>
          <div className="stack">
            {bookmarks.map((id) => {
              const q = QUESTION_BY_ID.get(id);
              if (!q) return null;
              return (
                <div key={id} className="card flat small">
                  <span className="chip" style={{ color: EXAM_BY_ID[q.exam].accent }}>{EXAM_BY_ID[q.exam].code}</span>{" "}
                  <span className="muted">{domainName(q)}</span>
                  <div className="mt" lang="en">{q.prompt}</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!Object.keys(p.questions).length && (
        <Empty icon={Sprout} title={t("Chưa có gì để ôn", "Nothing to review yet")}>
          <p>{t("Hãy làm vài câu luyện tập hoặc bài học — câu sai sẽ tự động xuất hiện ở đây.", "Do some practice or a lesson — missed questions will show up here automatically.")}</p>
        </Empty>
      )}
    </>
  );
}
